import type { ReactNode } from 'react';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';

import { Flex, FlexProps } from '@rover/kibble/core';
import useOnScrollEnd from '@rover/react-lib/src/hooks/useOnScrollEnd';

import type {
  OnScrollEventHooks,
  ScrollMarginProperties,
  ScrollPaddingProperties,
  ScrollSnapAlign,
  ScrollSnapStop,
  ScrollSnapType,
} from './types';

const DEFAULT_PADDING = '4px';

export type ChildrenProps = {
  /** The components to be rendered inside the carousel (e.g., cards) */
  children: ReactNode;
  /**
   * The scroll-snap-align property applied to each child element. Determines the snap point.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-align
   */
  scrollSnapAlign?: ScrollSnapAlign;
} & ScrollMarginProperties;

export type Props = {
  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-type
   */
  scrollSnapType?: ScrollSnapType;
  /**
   * Controls whether the scroll container must snap to a position after scrolling.
   * @see https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-stop
   */
  scrollSnapStop?: ScrollSnapStop;
  showScrollbar?: boolean;
  gap?: FlexProps['gap'];
} & ChildrenProps &
  ScrollPaddingProperties &
  OnScrollEventHooks;

const StyledCarouselItem = styled.div<ChildrenProps>`
  scroll-snap-align: ${(props) => props.scrollSnapAlign};
  flex-shrink: 0;

  scroll-margin-top: ${(props) => props.scrollMarginTop};
  scroll-margin-right: ${(props) => props.scrollMarginRight};
  scroll-margin-bottom: ${(props) => props.scrollMarginBottom};
  scroll-margin-left: ${(props) => props.scrollMarginLeft};
`;

const ScrollContainer = styled(Flex)<{ hideScrollbar?: boolean }>`
  ${(props) =>
    props.hideScrollbar
      ? `
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  `
      : ''}
`;

/**
 * A flexible carousel component that leverages CSS Scroll Snap for smooth,
 * native-like scrolling and snapping behavior. It handles child layout and spacing,
 * and its core snapping features are configurable via props.
 */

const BaseCarousel = forwardRef<HTMLElement, Props>(
  (
    {
      children,
      scrollSnapAlign = 'none',
      scrollSnapType = 'none',
      scrollSnapStop = 'normal',
      gap = '3x', // Note: scrollMargins should be less than or equal to gap
      scrollPaddingTop = DEFAULT_PADDING,
      scrollPaddingRight = DEFAULT_PADDING,
      scrollPaddingBottom = DEFAULT_PADDING,
      scrollPaddingLeft = DEFAULT_PADDING,
      showScrollbar = false,
      onScroll = () => {},
      onScrollEnd = () => {},
      ...childrenProps
    },
    // This ref is not guaranteed to be passed, and is only passed when wrapping this component with the PaginationWrapper
    // This ref is used to allow pagination wrapper to call the `onScroll` defined for the carousel without overriding it.
    // Note: Reviewing PaginationWrapper component will clarify any questions around this ref and the useImperativeHandle
    ref
  ) => {
    // We create a default ref to use to declare the onScrollEnd with for the carousel
    const scrollContainerRef = useRef<HTMLElement | null>(null);

    useOnScrollEnd(scrollContainerRef, onScrollEnd);

    // We use useImperativeHandle to expose the internal ref's DOM node to the passed in ref.
    // This handles cases where the BaseCarousel may not always get a ref passed to it, allowing us to guarantee onScrollEnd is set properly.
    useImperativeHandle(ref, () => scrollContainerRef.current!, []);

    return (
      <ScrollContainer
        data-testid="scroll-container"
        overflowX="auto"
        gap={gap}
        onScroll={onScroll}
        sx={{
          'scroll-snap-type': scrollSnapType,
          'scroll-snap-stop': scrollSnapStop,
          'scroll-padding-top': scrollPaddingTop,
          'scroll-padding-bottom': scrollPaddingBottom,
          'scroll-padding-left': scrollPaddingLeft,
          'scroll-padding-right': scrollPaddingRight,
          // We set the padding identical to scroll-padding to ensure the scroll boundary matches
          'padding-top': scrollPaddingTop,
          'padding-bottom': scrollPaddingBottom,
          'padding-left': scrollPaddingLeft,
          'padding-right': scrollPaddingRight,
        }}
        hideScrollbar={!showScrollbar}
        htmlRef={scrollContainerRef}
      >
        {React.Children.map(children, (child, index) => (
          <StyledCarouselItem scrollSnapAlign={scrollSnapAlign} {...childrenProps} key={index}>
            {child}
          </StyledCarouselItem>
        ))}
      </ScrollContainer>
    );
  }
);

export default BaseCarousel;
